import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import { useWhatsAppTemplates } from "../use-whatsapp-templates.js";
import { TemplateManagementSheet } from "../template-management/TemplateManagementSheet/index.js";
import { mapTemplateManagementError } from "../template-management/template-management-errors.js";
import {
  shouldKeepTemplateAfterDelete,
  shouldRemoveTemplateAfterDelete,
} from "../template-management/template-management-form.js";
import { buildTemplateLanguageOptions } from "../template-management/template-language-options.js";
import {
  TEMPLATE_LIST_FILTER_ALL,
  collectTemplateListFilterOptions,
  emptyTemplateListFilters,
  filterTemplateListItems,
  summarizeTemplateLibraryStatuses,
} from "../template-list-presentation.js";
import {
  cancelTemplateListDelete,
  initialTemplateListActionState,
  openTemplateListCreate,
  openTemplateListDuplicate,
  openTemplateListEdit,
  requestTemplateListDelete,
  setTemplateListSheetOpen,
} from "../template-list-action-state.js";
import { cn } from "../../../lib/utils.js";
import { TemplateListContent } from "./TemplateListContent.js";
import { TemplateListHeader } from "./TemplateListHeader.js";

export interface MessageTemplatesSectionProps {
  organizationId: number;
  canManage: boolean;
  /** Provider sync is only available while WhatsApp is connected. */
  canSync: boolean;
  /** Workspace shell already provides the title/tabs; also flatten the outer card chrome. */
  showHeading?: boolean;
  callingManagerUrl?: string | null;
}

function formatSyncedAt(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function MessageTemplatesSection({
  organizationId,
  canManage,
  canSync,
  showHeading = true,
  callingManagerUrl = null,
}: MessageTemplatesSectionProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const {
    loadState,
    items,
    lastSyncedAt,
    loadError,
    syncing,
    syncError,
    clearSyncError,
    refreshFromProvider,
    reload,
    upsertTemplateItem,
    removeTemplateItem,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    attachHeaderImage,
  } = useWhatsAppTemplates(organizationId, canSync, t("whatsapp.unreachable"));

  const [actionState, setActionState] = useState(initialTemplateListActionState);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [filters, setFilters] = useState(emptyTemplateListFilters);

  const showManageActions = canManage && canSync;
  const showRefresh = canManage && canSync;
  const languageOptions = buildTemplateLanguageOptions(i18n.language);
  const filterOptions = collectTemplateListFilterOptions(items);
  const effectiveFilters = {
    query: filters.query,
    status:
      filters.status === TEMPLATE_LIST_FILTER_ALL || filterOptions.statuses.includes(filters.status)
        ? filters.status
        : TEMPLATE_LIST_FILTER_ALL,
    category:
      filters.category === TEMPLATE_LIST_FILTER_ALL ||
      filterOptions.categories.includes(filters.category)
        ? filters.category
        : TEMPLATE_LIST_FILTER_ALL,
    language:
      filters.language === TEMPLATE_LIST_FILTER_ALL ||
      filterOptions.languages.includes(filters.language)
        ? filters.language
        : TEMPLATE_LIST_FILTER_ALL,
  };
  const visibleItems = filterTemplateListItems(items, effectiveFilters);
  const lastSyncedLabel = lastSyncedAt
    ? t("whatsapp.templates.lastSynced", {
        time: formatSyncedAt(lastSyncedAt, i18n.language),
      })
    : null;

  const { sheetOpen, sheetMode, deleteTarget } = actionState;

  function openCreate(): void {
    setActionState(openTemplateListCreate);
  }

  function openEdit(template: WhatsAppTemplateDto): void {
    setActionState((state) => openTemplateListEdit(state, template));
  }

  function openDuplicate(template: WhatsAppTemplateDto): void {
    setActionState((state) => openTemplateListDuplicate(state, template));
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const item = await deleteTemplate(deleteTarget.id);
      if (shouldKeepTemplateAfterDelete(item)) {
        upsertTemplateItem(item);
      } else if (shouldRemoveTemplateAfterDelete(item)) {
        removeTemplateItem(item.id);
      } else {
        reload();
      }
      setActionState(cancelTemplateListDelete);
    } catch (error) {
      setDeleteError(mapTemplateManagementError(t, error, t("whatsapp.unreachable")));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section
      className={cn(
        "overflow-hidden rounded-lg border border-line bg-surface",
        showHeading && "shadow-sm",
      )}
      aria-labelledby="whatsapp-templates-heading"
    >
      <TemplateListHeader
        showManageActions={showManageActions}
        showRefresh={showRefresh}
        syncing={syncing}
        filters={effectiveFilters}
        statusOptions={filterOptions.statuses}
        categoryOptions={filterOptions.categories}
        languageFilterValues={filterOptions.languages}
        languageOptions={languageOptions}
        lastSyncedLabel={lastSyncedLabel}
        templateCount={loadState === "ready" ? items.length : null}
        statusSummary={
          loadState === "ready"
            ? summarizeTemplateLibraryStatuses(items.map((item) => item.status))
            : null
        }
        onFiltersChange={setFilters}
        onCreate={openCreate}
        onRefresh={() => {
          void refreshFromProvider();
        }}
      />

      <TemplateListContent
        loadState={loadState}
        items={items}
        visibleItems={visibleItems}
        filters={effectiveFilters}
        lastSyncedAt={lastSyncedAt}
        loadError={loadError}
        syncing={syncing}
        syncError={syncError}
        canManage={canManage}
        showManageActions={showManageActions}
        showRefresh={showRefresh}
        languageOptions={languageOptions}
        onCreate={openCreate}
        onEdit={openEdit}
        onDuplicate={openDuplicate}
        onDelete={(template) => {
          setDeleteError(null);
          setActionState((state) => requestTemplateListDelete(state, template));
        }}
        onCancelDelete={() => {
          setDeleteError(null);
          setActionState(cancelTemplateListDelete);
        }}
        onConfirmDelete={() => {
          void handleDelete();
        }}
        pendingDeleteId={deleteTarget?.id ?? null}
        deleting={deleting}
        deleteError={deleteError}
        onRefresh={() => {
          void refreshFromProvider();
        }}
        onReload={reload}
        onDismissSyncError={clearSyncError}
        organizationId={organizationId}
        onAttachHeaderImage={
          showManageActions
            ? async (templateId, file) => {
                await attachHeaderImage(templateId, file);
              }
            : undefined
        }
      />

      <TemplateManagementSheet
        open={sheetOpen}
        mode={sheetMode}
        organizationId={organizationId}
        unreachableMessage={t("whatsapp.unreachable")}
        callingManagerUrl={callingManagerUrl}
        onOpenChange={(open) => {
          setActionState((state) => setTemplateListSheetOpen(state, open));
        }}
        onCreate={async (payload, headerImage) => {
          await createTemplate(payload, { headerImage });
        }}
        onEdit={async (templateId, payload) => {
          await updateTemplate(templateId, payload);
        }}
      />
    </section>
  );
}
